import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Dialog,
  useMediaQuery,
  IconButton,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LoadingLogoSkeleton from '../SkeletonLoading/loadingLogoSkeleton';
import { requestDualAccount } from '../../services/advisor_services';
import { useAccount } from '../../contexts/Account';
import { Auth } from 'aws-amplify';
import { AccountType } from '../../services/utils/types';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import AdvisoryCloudLogo from '../AdvisoryCloudLogo';
import eventBus from '../../eventBus';

const useStyles = ({ isXS }) => ({
  dialog: {
    width: isXS ? null : '600px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  dialogActions: {
    width: { xxs: '100%', sm: '300px' },
    padding: { xxs: '0px 24px', sm: '0px' },
    flexDirection: 'column',
    justifyContent: 'center',
    alignSelf: 'center',
  },
  mainButton: {
    padding: { xxs: '15px 30px 15px 30px', sm: '15px 30px 15px' },
    textTransform: 'none',
    fontWeight: 'bold',
    fontSize: '14px',
    height: '40px',
    borderRadius: '40px',
    width: '100%',
  },
});

export default function DualAccountModalB2B() {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { accountDetails, authenticate, asLoggedUserDetails } = useAccount();
  const history = useHistory();

  const boardOwner = accountDetails.contacts.find(
    (contact) => contact.isBoardOwner,
  );
  const { executeRecaptcha } = useGoogleReCaptcha();

  function handleClose() {
    if (isLoading) {
      return;
    }
    setOpen(false);
  }

  async function handleDualAccountRequest() {
    setIsLoading(true);
    try {
      if (!executeRecaptcha) {
        console.log('Execute recaptcha not yet available');
        return;
      }
      const token = await executeRecaptcha('signUp');

      await requestDualAccount({
        companyName: accountDetails.companyName,
        email: boardOwner.email,
        givenName: boardOwner.givenName,
        phoneNumber: boardOwner.phoneNumber?.phoneNumber || '5555555555',
        phoneCountryCode: boardOwner.phoneNumber?.countryCode || 'US',
        recurlyPlanCode: 'dual_plan_code',
        surName: boardOwner.surName,
        urlOfCheckoutPage: window.location.href,
        postalCode: accountDetails.postalCode,
        recaptchaToken: token,
      });

      const user = await Auth.currentAuthenticatedUser();
      await Auth.updateUserAttributes(user, {
        profile: AccountType.ADVISOR,
      });
      await Auth.currentAuthenticatedUser({ bypassCache: true });
      await authenticate();
      history.push('/advisor/aobf');
    } catch (error) {
      console.log({ error });
    }
  }

  useEffect(() => {
    eventBus.on('b2bDualModal', () => {
      setOpen(true);
    });
    return () => {
      eventBus.remove('b2bDualModal');
    };
  }, []);

  const isXS = useMediaQuery((theme) => theme.breakpoints.down('xs'));
  const classes = useStyles({ isXS });

  if (!boardOwner || boardOwner.id !== asLoggedUserDetails.userId) {
    return null;
  }

  return (
    <Dialog
      open={open}
      fullScreen={isXS}
      PaperProps={{
        sx: {
          borderRadius: { xxs: 0, sm: '10px' },
          padding: { xxs: '12px 0px 20px', sm: '16px 40px 24px' },
        },
      }}
      onClose={handleClose}
    >
      {isXS && (
        <IconButton
          size="small"
          onClick={handleClose}
          sx={{ position: 'absolute', top: 0, right: 0 }}
        >
          <CloseIcon />
        </IconButton>
      )}
      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <AdvisoryCloudLogo
          removeHyperlink
          onClick={() => {}}
          width={190}
          height={34}
        />
      </Box>
      <DialogContent sx={classes.dialog}>
        <Typography
          textAlign="center"
          pb={2}
          sx={{
            fontWeight: '400',
            fontSize: '14px',
            color: '#001030',
            paddingBottom: '24px',
          }}
        >
          To start exploring advisory boards on our platform, please click below
          to create your advisor profile. You will then be able to browse other
          advisory boards on AdvisoryCloud.
        </Typography>
      </DialogContent>
      <DialogActions sx={classes.dialogActions}>
        <Button
          id="dualAccountB2BButton"
          variant="contained"
          onClick={handleDualAccountRequest}
          sx={classes.mainButton}
          disabled={isLoading}
        >
          {isLoading ? (
            <Box
              sx={{ width: '96px', display: 'flex', justifyContent: 'center' }}
            >
              <LoadingLogoSkeleton />
            </Box>
          ) : (
            'Access Advisory Boards'
          )}
        </Button>
        <Button
          id="dualAccountLearnMoreButton"
          color="primary"
          variant="text"
          onClick={() => {}}
          sx={{
            ...classes.mainButton,
            fontWeight: 'normal',
            marginTop: '12px',
          }}
          disabled={isLoading}
        >
          Learn More
        </Button>
      </DialogActions>
    </Dialog>
  );
}
